/**
 * Page table of contents.
 *
 * Fills the static ToC (left gutter) and the floating popover with links to
 * every H2 inside .content-blocks, tracks the active section, and swaps the
 * static list for a floating button once the list scrolls out of view.
 */
export default function pageToc() {
	const staticNav = document.querySelector( '[data-page-toc-static]' );
	const sentinel = document.querySelector( '[data-page-toc-sentinel]' );
	const floating = document.querySelector( '[data-page-toc-floating]' );

	if ( ! staticNav || ! sentinel || ! floating ) {
		return;
	}

	const staticList = staticNav.querySelector( '[data-page-toc-list]' );
	const panel = floating.querySelector( '[data-page-toc-panel]' );
	const panelList = floating.querySelector( '[data-page-toc-panel-list]' );
	const button = floating.querySelector( '[data-page-toc-button]' );
	const wrapper = document.querySelector( '.content-blocks' );
	const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	// Collect the page's visible H2 headings and give them stable anchor ids.
	const usedIds = [];
	const headings = Array.from( wrapper.querySelectorAll( 'h2' ) ).filter( ( heading ) => {
		return ! heading.closest( '[hidden], template, .page-toc, .page-toc-floating' ) && heading.textContent.trim() !== '';
	} );

	if ( ! headings.length ) {
		return;
	}

	headings.forEach( ( heading ) => {
		if ( heading.id ) {
			usedIds.push( heading.id );
			return;
		}

		const base = ( heading.textContent.trim().toLowerCase()
			.normalize( 'NFD' ).replace( /[\u0300-\u036f]/g, '' )
			.replace( /[^a-z0-9]+/g, '-' )
			.replace( /^-+|-+$/g, '' ) ) || 'sectie';
		let id = base;
		let counter = 2;

		while ( usedIds.includes( id ) || document.getElementById( id ) ) {
			id = base + '-' + counter;
			counter++;
		}

		usedIds.push( id );
		heading.id = id;
	} );

	// Build both lists with the same items.
	function buildList( list ) {
		headings.forEach( ( heading, index ) => {
			const item = document.createElement( 'li' );
			item.className = 'page-toc__item';
			item.dataset.index = index;

			const link = document.createElement( 'a' );
			link.className = 'page-toc__link';
			link.href = '#' + heading.id;
			link.textContent = heading.textContent.trim();

			item.appendChild( link );
			list.appendChild( item );
		} );
	}

	buildList( staticList );
	buildList( panelList );

	// Position the static list at the start of the content: the height of the
	// first H2, or of an explicit anchor when a block provides one (the detail
	// page hero marks its text area, so on inspiration posts the list lines up
	// right under the hero photo). A page can open with any block — a full-width hero photo, a
	// two-column content block, a slider — so everything that draws inside the
	// list's horizontal band counts as an obstruction (images, sliders and real
	// text; wrappers that only paint a background do not). Is that single spot
	// not free, the list stays hidden and the floating button takes over right
	// away: the list never slides down past blocks.
	const wideViewport = window.matchMedia( '(min-width: 87.5rem)' );
	const anchor = wrapper.querySelector( '[data-page-toc-anchor]' ) || headings[ 0 ];
	let forceFloating = false;

	function positionStatic() {
		if ( ! wideViewport.matches ) {
			forceFloating = false;
			return;
		}

		// Measure at the spot itself, invisibly if the list was hidden.
		staticNav.style.visibility = 'hidden';
		staticNav.style.display = 'block';

		const wrapperRect = wrapper.getBoundingClientRect();
		const navRect = staticNav.getBoundingClientRect();
		const margin = 24;
		// With an explicit anchor, whatever ends above it (the hero photo) is
		// not in the way: the list starts exactly at the anchor.
		const marginAbove = anchor === headings[ 0 ] ? margin : 0;
		const top = anchor.getBoundingClientRect().top - wrapperRect.top;

		const blocked = Array.from( wrapper.querySelectorAll( 'img, svg, video, iframe, figure, .swiper, h1, h2, h3, h4, p, ul, ol, table, blockquote, a, button' ) ).some( ( element ) => {
			if ( element.closest( '.page-toc, .page-toc-floating' ) ) {
				return false;
			}

			const rect = element.getBoundingClientRect();

			if ( ! rect.width || ! rect.height || rect.right <= navRect.left || rect.left >= navRect.left + navRect.width ) {
				return false;
			}

			const elementTop = rect.top - wrapperRect.top;
			const elementBottom = rect.bottom - wrapperRect.top;

			return elementTop < top + navRect.height + margin && elementBottom > top - marginAbove;
		} );

		staticNav.style.insetBlockStart = Math.round( top ) + 'px';
		staticNav.style.display = blocked ? 'none' : '';
		staticNav.style.visibility = '';
		forceFloating = blocked;

		if ( blocked ) {
			floating.hidden = false;
		}

		// The floating button also takes over once this spot scrolls out above.
		sentinel.style.position = 'absolute';
		sentinel.style.insetBlockStart = Math.round( top ) + 'px';
	}

	staticNav.hidden = false;
	positionStatic();

	let repositionTimer = null;

	function requestReposition() {
		window.clearTimeout( repositionTimer );
		repositionTimer = window.setTimeout( () => {
			positionStatic();
			sentinelObserver.unobserve( sentinel );
			sentinelObserver.observe( sentinel );
		}, 150 );
	}

	window.addEventListener( 'resize', requestReposition );
	window.addEventListener( 'load', requestReposition );
	wideViewport.addEventListener( 'change', requestReposition );

	// Track the active section, mirroring the day-program ToC states.
	function setActiveSection( index ) {
		document.querySelectorAll( '.page-toc__item' ).forEach( ( item ) => {
			const itemIndex = Number( item.dataset.index );
			item.classList.remove( 'is-active', 'is-visited' );

			if ( itemIndex < index ) {
				item.classList.add( 'is-visited' );
			} else if ( itemIndex === index ) {
				item.classList.add( 'is-active', 'is-visited' );
			}
		} );
	}

	// The last heading above the 40% viewport line is the active section.
	// A scroll listener (rAF-throttled) instead of an IntersectionObserver:
	// one-line headings jump straight past an observer band on fast scrolls.
	let activeTicking = false;

	function updateActiveSection() {
		const line = window.innerHeight * 0.4;
		let index = 0;

		headings.forEach( ( heading, headingIndex ) => {
			if ( heading.getBoundingClientRect().top <= line ) {
				index = headingIndex;
			}
		} );

		setActiveSection( index );
		activeTicking = false;
	}

	window.addEventListener( 'scroll', () => {
		if ( ! activeTicking ) {
			activeTicking = true;
			window.requestAnimationFrame( updateActiveSection );
		}
	}, { passive: true } );

	updateActiveSection();

	// Floating button: appears once the static ToC position scrolls out above.
	function openPanel() {
		panel.classList.add( 'is-open' );
		button.setAttribute( 'aria-expanded', 'true' );

		const first = panel.querySelector( '.page-toc__link' );
		if ( first ) {
			first.focus( { preventScroll: true } );
		}
	}

	function closePanel( refocus ) {
		if ( ! panel.classList.contains( 'is-open' ) ) {
			return;
		}

		panel.classList.remove( 'is-open' );
		button.setAttribute( 'aria-expanded', 'false' );

		if ( refocus ) {
			button.focus( { preventScroll: true } );
		}
	}

	const sentinelObserver = new IntersectionObserver( ( entries ) => {
		entries.forEach( ( entry ) => {
			if ( forceFloating ) {
				floating.hidden = false;
				return;
			}

			const scrolledPast = ! entry.isIntersecting && entry.boundingClientRect.top < 0;
			floating.hidden = ! scrolledPast;

			if ( ! scrolledPast ) {
				closePanel( false );
			}
		} );
	} );

	sentinelObserver.observe( sentinel );

	button.addEventListener( 'click', () => {
		if ( panel.classList.contains( 'is-open' ) ) {
			closePanel( true );
		} else {
			openPanel();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key ) {
			closePanel( true );
		}
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( ! floating.contains( event.target ) ) {
			closePanel( false );
		}
	} );

	// Smooth-scroll ToC links (both lists); close the panel on navigation.
	document.addEventListener( 'click', ( event ) => {
		const link = event.target.closest( '.page-toc__link' );

		if ( ! link ) {
			return;
		}

		const target = document.getElementById( link.hash.slice( 1 ) );

		if ( ! target ) {
			return;
		}

		event.preventDefault();
		target.scrollIntoView( {
			behavior: reducedMotion.matches ? 'auto' : 'smooth',
			block: 'start',
		} );
		history.pushState( null, '', link.hash );
		closePanel( false );
	} );
}
